<template>
    <div class="app-navbar-item ms-1 ms-md-4" ref="menuContainer">
        <!--begin::Menu wrapper-->
        <div class="btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px position-relative"
             data-kt-menu-trigger="click"
             data-kt-menu-attach="parent"
             data-kt-menu-placement="bottom-end"
             id="kt_menu_item_notification"
             @click="handleMenuClick">
            <KTIcon icon-name="notification" icon-class="fs-1" />
            <span v-if="unreadCount > 0"
                  class="badge badge-sm badge-circle badge-light-danger position-absolute translate-middle"
                  style="
          font-size: 0.8rem;
          font-weight: bold;
          right: -10px;
          top: 4px;
          background-color: #f1416c;
          color: white;
          box-shadow: 0 0 0 2px #fff;
        ">
                {{ unreadCount > 99 ? "99+" : unreadCount }}
            </span>
        </div>

        <!--begin::Menu-->
    <div class="menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-375px show" style="z-index: 105; position: fixed; inset: 0px 0px auto auto; margin: 0px; transform: translate(-78px, 74px);"
             data-kt-menu="true"
             v-if="isMenuOpen">
            <!--begin::Heading-->
            <div class="d-flex flex-column bgi-no-repeat rounded-top"
                 :style="`background-image: url('${getAssetPath(
          '/media/misc/menu-header-bg.jpg'
        )}')`">
                <!--begin::Title-->
                <h3 class="text-white fw-semibold px-9 mt-10 mb-6">
                    Thông báo
                    <span v-if="unreadCount > 0" class="fs-8 opacity-75 ps-3">{{ unreadCount }} chưa đọc</span>
                </h3>
                <!--end::Title-->
                <!--begin::Actions-->
                <div class="px-9 mb-5">
                    <button v-if="hasUnread"
                            class="btn btn-sm btn-light-primary"
                            @click="markAllAsRead"
                            :disabled="loading">
                        Đánh dấu tất cả đã đọc
                    </button>
                </div>
                <!--end::Actions-->
            </div>
            <!--end::Heading-->
            <!--begin::Notifications content-->
            <div class="scroll-y mh-325px my-3 px-4"
                 ref="notificationContent"
                 @scroll="handleScroll">
                <!--begin::Loading state-->
                <div v-if="loading && notifications.length === 0"
                     class="text-center py-4">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Đang tải...</span>
                    </div>
                </div>
                <!--end::Loading state-->
                <!--begin::Empty state-->
                <div v-else-if="notifications.length === 0"
                     class="text-center py-4 text-gray-600">
                    <KTIcon icon-name="notification"
                            icon-class="fs-2x text-gray-400 mb-2" />
                    <div class="fw-semibold">Không có thông báo nào</div>
                </div>
                <!--end::Empty state-->
                <!--begin::Notification items-->
                <template v-else>
                    <div v-for="notification in notifications"
                         :key="notification.id"
                         class="d-flex flex-stack py-4 px-2 notification-item"
                         :class="{ unread: !notification.isRead }"
                         @click="handleNotificationClick(notification)">
                        <!--begin::Section-->
                        <div class="d-flex align-items-center">
                            <!--begin::Symbol-->
                            <div class="symbol symbol-40px me-4">
                                <span class="symbol-label rounded-circle"
                                      :class="
                  notification.isRead
                    ? 'bg-light-info'
                    : 'bg-primary'
                ">
                                    <KTIcon icon-name="notification-status"
                                            :icon-class="
                    notification.isRead ? 'text-info fs-3' : 'text-white fs-3'
                  " />
                                </span>
                            </div>
                            <!--end::Symbol-->
                            <!--begin::Title-->
                            <div class="mb-0 me-2 flex-grow-1">
                                <div class="fs-6 text-hover-primary fw-bold mb-1"
                                     :class="{
                  'fw-bolder text-gray-900': !notification.isRead,
                  'text-gray-700': notification.isRead
                }"
                                     v-html="notification.title"></div>
                                <div class="text-gray-700 fs-7 line-clamp-2"
                                     v-html="notification.content"></div>
                                <div class="text-gray-500 fs-8 mt-1">
                                    {{ formatDateTime(notification.createdDate) }}
                                </div>
                            </div>
                            <!--end::Title-->
                        </div>
                        <!--end::Section-->
                        <!--begin::Badge-->
                        <div v-if="!notification.isRead"
                             class="badge badge-primary rounded-pill px-3 py-1"
                             style="font-size: 0.75rem; font-weight: bold;">
                            Mới
                        </div>
                        <!--end::Badge-->
                    </div>
                </template>
                <!--end::Notification items-->
                <!--begin::Load more-->
                <div v-if="hasMore && loading && notifications.length > 0"
                     class="text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary"
                         role="status">
                        <span class="visually-hidden">Đang tải thêm...</span>
                    </div>
                </div>
                <!--end::Load more-->
            </div>
            <!--end::Notifications content-->
            <!--begin::View more-->
            <div class="py-3 text-center border-top">
                <NuxtLink to="/notifications"
                          class="btn btn-color-gray-600 btn-active-color-primary">
                    Xem tất cả
                    <KTIcon icon-name="arrow-right" icon-class="fs-5" />
                </NuxtLink>
            </div>
            <!--end::View more-->
        </div>
        <!--end::Menu-->
        <!--end::Menu wrapper-->
    </div>
</template>

<script lang="ts">
    import { getAssetPath } from "@/core/helpers/assets";
    import { defineComponent, ref, onMounted, nextTick, onBeforeUnmount } from "vue";
    import { useNotification } from "@/composables/useNotification";
    import { useRouter } from "vue-router";
    import { formatDateTime } from "~/core/helpers/util";

    export default defineComponent({
        name: "notifications-menu",
        setup() {
            const router = useRouter();
            const notificationContent = ref<HTMLElement | null>(null);
            const menuContainer = ref<HTMLElement | null>(null);
            const isMenuOpen = ref(false);

            const {
                notifications,
                loading,
                hasMore,
                hasUnread,
                unreadCount,
                loadNotifications,
                loadMoreNotifications,
                markAsRead,
                markAllAsRead,
                loadUnreadCount,
                formatDate,
            } = useNotification();

            const handleScroll = () => {
                if (!notificationContent.value) return;

                const { scrollTop, scrollHeight, clientHeight } =
                    notificationContent.value;
                if (
                    scrollHeight - scrollTop - clientHeight < 50 &&
                    hasMore.value &&
                    !loading.value
                ) {
                    loadMoreNotifications();
                }
            };

            const handleNotificationClick = async (notification: any) => {
                await markAsRead(notification);

                if (notification.url) {
                    router.push(notification.url);
                }
            };

            const handleMenuClick = () => {
                isMenuOpen.value = !isMenuOpen.value;
                // Load notifications when menu is clicked
                loadNotifications();
                // Also refresh unread count
                loadUnreadCount();
            };

            const handleOutsideClick = (event: Event) => {
                if (menuContainer.value && !menuContainer.value.contains(event.target as Node)) {
                    isMenuOpen.value = false;
                }
            };

            loadUnreadCount();
            // Tự động load lại unread count mỗi 30 giây

            let interval: NodeJS.Timeout;
            onMounted(() => {
                interval = setInterval(() => {
                    loadUnreadCount();
                }, 10000);

                // Thêm event listener cho outside click
                document.addEventListener('click', handleOutsideClick);
            });

            onBeforeUnmount(() => {
                console.log("onBeforeUnmount");
                if (interval) {
                    clearInterval(interval);
                }
                // Xóa event listener
                document.removeEventListener('click', handleOutsideClick);
            });

            return {
                notifications,
                loading,
                hasMore,
                hasUnread,
                unreadCount,
                markAllAsRead,
                formatDate,
                handleScroll,
                handleNotificationClick,
                handleMenuClick,
                notificationContent,
                menuContainer,
                isMenuOpen,
                getAssetPath,
                formatDateTime,
            };
        },
    });
</script>

<style scoped>
    .notification-item {
        cursor: pointer;
        transition: all 0.2s ease;
        border-bottom: 1px solid #f1f1f2;
        border-radius: 8px;
        margin: 2px 0;
    }

        .notification-item:last-child {
            border-bottom: none;
        }

        .notification-item:hover {
            background-color: #f8f9fa;
            transform: translateX(2px);
        }

        .notification-item.unread {
            background-color: #e3f2fd;
            border-left: 4px solid #1976d2;
            margin-left: -8px;
            padding-left: 12px;
        }

    .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }
</style>
